import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Webhook, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';

interface WebhookItem {
  id: string;
  url: string;
  events: string[];
  status: string;
  secret?: string;
  lastTriggered?: number;
  last_triggered?: number;
  createdAt?: number;
  created_at?: number;
}

const AVAILABLE_EVENTS = [
  'LICENSE_CREATED',
  'LICENSE_ACTIVATED',
  'LICENSE_REVOKED',
  'LICENSE_EXPIRED',
];

function Webhooks() {
  const { t } = useTranslation();
  const [webhooks, setWebhooks] = useState<WebhookItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookItem | null>(null);
  const [newWebhook, setNewWebhook] = useState({
    url: '',
    events: [] as string[],
    secret: '',
    status: 'ACTIVE',
  });

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/webhooks`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTest = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      await fetch(`${apiUrl}/api/webhooks/${id}/test`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error('Failed to test webhook:', error);
    }
  };

  const handleCreateWebhook = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/webhooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newWebhook),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewWebhook({ url: '', events: [], secret: '', status: 'ACTIVE' });
        fetchWebhooks();
      }
    } catch (error) {
      console.error('Failed to create webhook:', error);
    }
  };

  const handleEditWebhook = async () => {
    if (!selectedWebhook) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/webhooks/${selectedWebhook.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          url: selectedWebhook.url,
          events: selectedWebhook.events,
          secret: selectedWebhook.secret,
          status: selectedWebhook.status,
        }),
      });

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedWebhook(null);
        fetchWebhooks();
      }
    } catch (error) {
      console.error('Failed to update webhook:', error);
    }
  };

  const openEditDialog = (webhook: WebhookItem) => {
    setSelectedWebhook({ ...webhook });
    setIsEditDialogOpen(true);
  };

  const toggleEvent = (event: string, isCreate: boolean) => {
    if (isCreate) {
      setNewWebhook((prev) => ({
        ...prev,
        events: prev.events.includes(event)
          ? prev.events.filter((e) => e !== event)
          : [...prev.events, event],
      }));
    } else if (selectedWebhook) {
      setSelectedWebhook((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          events: prev.events.includes(event)
            ? prev.events.filter((e) => e !== event)
            : [...prev.events, event],
        };
      });
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Configuration"
        icon={Webhook}
        title={t('webhooks.title')}
        subtitle={t('webhooks.subtitle')}
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('webhooks.create')}
          </Button>
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('webhooks.allWebhooks')}</CardTitle>
          <CardDescription>
            {t('webhooks.configured', { count: webhooks.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('webhooks.table.url')}</TableHead>
                <TableHead>{t('webhooks.table.events')}</TableHead>
                <TableHead>{t('webhooks.table.status')}</TableHead>
                <TableHead>{t('webhooks.table.lastTriggered')}</TableHead>
                <TableHead>{t('webhooks.table.created')}</TableHead>
                <TableHead className="text-right">{t('webhooks.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {webhooks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                webhooks.map((webhook) => (
                  <TableRow key={webhook.id}>
                    <TableCell className="font-mono text-xs max-w-xs break-words">
                      {webhook.url}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{webhook.events.length} {t('webhooks.table.events').toLowerCase()}</Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={webhook.status} />
                    </TableCell>
                    <TableCell>
                      {webhook.last_triggered || webhook.lastTriggered
                        ? formatDate(webhook.last_triggered || webhook.lastTriggered)
                        : t('licenses.never')}
                    </TableCell>
                    <TableCell>
                      {formatDate(webhook.created_at || webhook.createdAt)}
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTest(webhook.id)}
                      >
                        {t('webhooks.actions.test')}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(webhook)}>
                        {t('webhooks.actions.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Webhook Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('webhooks.dialogs.create.title')}</DialogTitle>
            <DialogDescription>
              {t('webhooks.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="url">{t('webhooks.table.url')}</Label>
              <Input
                id="url"
                type="url"
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                placeholder={t('webhooks.dialogs.urlPlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label>{t('webhooks.table.events')}</Label>
              <div className="space-y-2">
                {AVAILABLE_EVENTS.map((event) => (
                  <div key={event} className="flex items-center space-x-2">
                    <Checkbox
                      id={`event-${event}`}
                      checked={newWebhook.events.includes(event)}
                      onCheckedChange={() => toggleEvent(event, true)}
                    />
                    <label
                      htmlFor={`event-${event}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {event}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="secret">{t('webhooks.dialogs.secret')}</Label>
              <Input
                id="secret"
                value={newWebhook.secret}
                onChange={(e) => setNewWebhook({ ...newWebhook, secret: e.target.value })}
                placeholder={t('webhooks.dialogs.secretPlaceholder')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">{t('webhooks.table.status')}</Label>
              <Select
                value={newWebhook.status}
                onValueChange={(value) => setNewWebhook({ ...newWebhook, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">{t('licenses.active')}</SelectItem>
                  <SelectItem value="INACTIVE">{t('licenses.inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleCreateWebhook}
              disabled={!newWebhook.url || newWebhook.events.length === 0}
            >
              {t('webhooks.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Webhook Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('webhooks.dialogs.edit.title')}</DialogTitle>
            <DialogDescription>
              {t('webhooks.dialogs.edit.description')}
            </DialogDescription>
          </DialogHeader>
          {selectedWebhook && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-url">{t('webhooks.table.url')}</Label>
                <Input
                  id="edit-url"
                  type="url"
                  value={selectedWebhook.url}
                  onChange={(e) => setSelectedWebhook({ ...selectedWebhook, url: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label>{t('webhooks.table.events')}</Label>
                <div className="space-y-2">
                  {AVAILABLE_EVENTS.map((event) => (
                    <div key={event} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-event-${event}`}
                        checked={selectedWebhook.events.includes(event)}
                        onCheckedChange={() => toggleEvent(event, false)}
                      />
                      <label
                        htmlFor={`edit-event-${event}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {event}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-secret">{t('webhooks.dialogs.secret')}</Label>
                <Input
                  id="edit-secret"
                  value={selectedWebhook.secret || ''}
                  onChange={(e) => setSelectedWebhook({ ...selectedWebhook, secret: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-status">{t('webhooks.table.status')}</Label>
                <Select
                  value={selectedWebhook.status}
                  onValueChange={(value) => setSelectedWebhook({ ...selectedWebhook, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">{t('licenses.active')}</SelectItem>
                    <SelectItem value="INACTIVE">{t('licenses.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditWebhook}>
              {t('common.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Webhooks;
